const API_URL = process.env.REACT_APP_API_URL || 'https://0vr4s47ewc.execute-api.eu-west-1.amazonaws.com/Production';
const API_KEY = process.env.REACT_APP_API_KEY || 'BqbHsyh5broc3S5o9Er42Z54sTDYKTD1Gkhv6JIb';

// Helper function to add timeout to fetch requests
const fetchWithTimeout = (url, options = {}, timeout = 2000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    )
  ]);
};

export const saveSurvey = async (surveyData) => {
  const maxRetries = 5;
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Saving survey data via API Gateway (attempt ${attempt}/${maxRetries}):`, surveyData);
      
      const timestamp = new Date().toISOString();
      const dataWithTimestamp = {
        ...surveyData,
        id: Date.now().toString(),
        timestamp,
        httpMethod: 'POST'
      };
      
      const endpoint = surveyData.type === 'issue_report' ? '/issues' : '/surveys';
      
      const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'BqbHsyh5broc3S5o9Er42Z54sTDYKTD1Gkhv6JIb'
        },
        body: JSON.stringify({
          ...dataWithTimestamp,
          httpMethod: 'POST'
        })
      }, 2000);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      console.log('Survey saved successfully via API Gateway');
      return { success: true, message: 'Survey saved successfully' };
      
    } catch (error) {
      console.error(`Save attempt ${attempt} failed:`, error);
      lastError = error;
      
      if (attempt < maxRetries) {
        console.log(`Retrying in 1 second... (${maxRetries - attempt} attempts remaining)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  console.error('All retry attempts failed:', lastError);
  throw new Error('There is currently an issue submitting. Please try again later.');
};

export const checkDuplicateAddress = async (address) => {
  try {
    const surveysResponse = await fetchWithTimeout(`${API_URL}/surveys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'BqbHsyh5broc3S5o9Er42Z54sTDYKTD1Gkhv6JIb'
      },
      body: JSON.stringify({ httpMethod: 'GET' })
    }, 2000);
    
    if (!surveysResponse.ok) {
      throw new Error('Failed to fetch survey data');
    }
    
    const surveysRaw = await surveysResponse.json();
    
    // Parse the body if it's a stringified JSON
    let surveys;
    try {
      surveys = typeof surveysRaw.body === 'string' ? JSON.parse(surveysRaw.body) : surveysRaw;
    } catch (parseError) {
      surveys = surveysRaw;
    }
    
    const surveyItems = surveys.items || [];

    // Trim surrounding whitespace but preserve original casing — addresses are case-sensitive
    const normalizedAddress = address.trim().replace(/\s+/g, ' ');

    // Only check entries that belong to the same survey type
    const duplicate = surveyItems.find(item => {
      const itemType = item.surveyType || 'general';
      if (itemType !== surveyType) return false;
      const itemAddress = (item.address || '').trim().replace(/\s+/g, ' ');
      return itemAddress === normalizedAddress;
    });
    
    return duplicate ? true : false;
  } catch (error) {
    console.error('Error checking for duplicate address:', error);
    // In case of error, allow submission to continue
    return false;
  }
};

export const getSurveyData = async () => {
  const maxRetries = 5;
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Fetching survey data from API Gateway (attempt ${attempt}/${maxRetries})`);
      
      const [surveysResponse, issuesResponse] = await Promise.all([
        fetchWithTimeout(`${API_URL}/surveys`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': 'BqbHsyh5broc3S5o9Er42Z54sTDYKTD1Gkhv6JIb'
          },
          body: JSON.stringify({ httpMethod: 'GET' })
        }, 2000),
        fetchWithTimeout(`${API_URL}/issues`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': 'BqbHsyh5broc3S5o9Er42Z54sTDYKTD1Gkhv6JIb'
          },
          body: JSON.stringify({ httpMethod: 'GET' })
        }, 2000)
      ]);
      
      if (!surveysResponse.ok || !issuesResponse.ok) {
        throw new Error(`API request failed`);
      }
      
      const [surveysRaw, issuesRaw] = await Promise.all([
        surveysResponse.json(),
        issuesResponse.json()
      ]);
      
      let surveys, issues;
      
      try {
        surveys = typeof surveysRaw.body === 'string' ? JSON.parse(surveysRaw.body) : surveysRaw;
        issues = typeof issuesRaw.body === 'string' ? JSON.parse(issuesRaw.body) : issuesRaw;
      } catch (parseError) {
        console.error('Error parsing response body:', parseError);
        surveys = surveysRaw;
        issues = issuesRaw;
      }

      console.log('Survey data fetched successfully from API Gateway');
      return {
        surveys: surveys.items || [],
        issues: issues.items || []
      };
      
    } catch (error) {
      console.error(`Fetch attempt ${attempt} failed:`, error);
      lastError = error;
      
      if (attempt < maxRetries) {
        console.log(`Retrying in 1 second... (${maxRetries - attempt} attempts remaining)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  console.error('All fetch retry attempts failed:', lastError);
  throw new Error('There is currently an issue loading data. Please try again later.');
};

export const saveCase = async (caseData) => {
  const maxRetries = 5;
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const timestamp = new Date().toISOString();
      const dataWithTimestamp = {
        ...caseData,
        id: Date.now().toString(),
        timestamp,
        httpMethod: 'POST'
      };
      
      const response = await fetchWithTimeout(`${API_URL}/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        },
        body: JSON.stringify(dataWithTimestamp)
      }, 2000);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      return { success: true, message: 'Case saved successfully' };
      
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  throw new Error('Failed to save case. Please try again later.');
};

export const getCaseByReference = async (caseReference) => {
  try {
    const response = await fetchWithTimeout(`${API_URL}/cases`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY
      },
      body: JSON.stringify({ httpMethod: 'GET', caseReference })
    }, 2000);
    
    if (!response.ok) {
      throw new Error('Failed to fetch case');
    }
    
    const result = await response.json();
    const data = typeof result.body === 'string' ? JSON.parse(result.body) : result;
    return data.item || null;
  } catch (error) {
    console.error('Error fetching case:', error);
    throw error;
  }
};

export const updateCase = async (caseReference, updateData) => {
  const maxRetries = 5;
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        },
        body: JSON.stringify({
          httpMethod: 'PUT',
          caseReference,
          ...updateData
        })
      }, 2000);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      return { success: true, message: 'Case updated successfully' };
      
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  throw new Error('Failed to update case. Please try again later.');
};

export const saveLegalSurvey = async (surveyData) => {
  const maxRetries = 5;
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Saving legal survey (attempt ${attempt}/${maxRetries}):`, surveyData);

      const dataWithTimestamp = {
        ...surveyData,
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        httpMethod: 'POST'
      };

      const response = await fetchWithTimeout(`${API_URL}/legal-surveys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        },
        body: JSON.stringify(dataWithTimestamp)
      }, 2000);

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      console.log('Legal survey saved successfully');
      return { success: true, message: 'Legal survey saved successfully' };

    } catch (error) {
      console.error(`Save attempt ${attempt} failed:`, error);
      lastError = error;

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  throw new Error('There is currently an issue submitting. Please try again later.');
};

export const checkDuplicateLegalAddress = async (address) => {
  try {
    const response = await fetchWithTimeout(`${API_URL}/legal-surveys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY
      },
      body: JSON.stringify({ httpMethod: 'GET' })
    }, 2000);

    if (!response.ok) {
      throw new Error('Failed to fetch legal survey data');
    }

    const raw = await response.json();

    let data;
    try {
      data = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;
    } catch {
      data = raw;
    }

    const items = data.items || [];

    // Case-sensitive match — trim whitespace only
    const normalizedAddress = address.trim().replace(/\s+/g, ' ');

    const duplicate = items.find(item => {
      const itemAddress = (item.address || '').trim().replace(/\s+/g, ' ');
      return itemAddress === normalizedAddress;
    });

    return duplicate ? true : false;
  } catch (error) {
    console.error('Error checking for duplicate legal address:', error);
    // Allow submission to continue if the check fails
    return false;
  }
};

export const getLegalSurveyData = async () => {
  const maxRetries = 5;
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Fetching legal survey data (attempt ${attempt}/${maxRetries})`);

      const response = await fetchWithTimeout(`${API_URL}/legal-surveys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        },
        body: JSON.stringify({ httpMethod: 'GET' })
      }, 2000);

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const raw = await response.json();
      let data;
      try {
        data = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;
      } catch {
        data = raw;
      }

      return data.items || [];

    } catch (error) {
      console.error(`Fetch attempt ${attempt} failed:`, error);
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  throw new Error('There is currently an issue loading legal survey data. Please try again later.');
};
